import { Button, buttonVariants } from '@rekrytering/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@rekrytering/ui/components/dropdown-menu'
import { cn } from '@rekrytering/ui/lib/utils'
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router'
import {
  Check,
  ChevronDown,
  ChevronUp,
  LogOut,
  MessageSquare,
  Settings,
  Slash,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { NotificationBell } from '@/features/notifications/components/notification-bell'
import { Route as CandidatesRoute } from '@/routes/_app/_auth/dashboard/_layout.candidates'
import { Route as DashboardRoute } from '@/routes/_app/_auth/dashboard/_layout.index'
import { Route as PipelineReportRoute } from '@/routes/_app/_auth/dashboard/_layout.pipeline-report'
import { Route as QuestionBankRoute } from '@/routes/_app/_auth/dashboard/_layout.question-bank'
import { Route as ReferralsRoute } from '@/routes/_app/_auth/dashboard/_layout.referrals'
import { Route as BillingSettingsRoute } from '@/routes/_app/_auth/dashboard/_layout.settings.billing'
import { Route as SettingsRoute } from '@/routes/_app/_auth/dashboard/_layout.settings.index'
import { Route as TemplatesRoute } from '@/routes/_app/_auth/dashboard/_layout.templates'
import { Route as TimeToHireRoute } from '@/routes/_app/_auth/dashboard/_layout.time-to-hire'
import { useSignOut } from '@/shared/hooks/use-sign-out'
import { Image } from '@/shared/ui/image'
import { LanguageSwitcher } from '@/shared/ui/language-switcher'
import { Logo } from '@/shared/ui/logo'
import { ThemeSwitcher } from '@/shared/ui/theme-switcher'
import type { User } from '~/types'

export function Navigation({
  user,
  isChatOpen,
  onChatToggle,
}: {
  user: User
  isChatOpen: boolean
  onChatToggle: () => void
}) {
  const { t } = useTranslation()
  const signOut = useSignOut()
  const matchRoute = useMatchRoute()
  const navigate = useNavigate()
  const isDashboardPath = matchRoute({
    to: DashboardRoute.fullPath,
    fuzzy: true,
  })
  const isCandidatesPath = matchRoute({ to: CandidatesRoute.fullPath })
  const isQuestionBankPath = matchRoute({ to: QuestionBankRoute.fullPath })
  const isReferralsPath = matchRoute({ to: ReferralsRoute.fullPath })
  const isPipelineReportPath = matchRoute({ to: PipelineReportRoute.fullPath })
  const isTimeToHirePath = matchRoute({ to: TimeToHireRoute.fullPath })
  const isTemplatesPath = matchRoute({ to: TemplatesRoute.fullPath })
  const isSettingsPath = matchRoute({ to: SettingsRoute.fullPath })
  const isBillingPath = matchRoute({ to: BillingSettingsRoute.fullPath })

  if (!user) {
    return null
  }

  return (
    <nav className="sticky top-0 z-50 flex w-full flex-col border-b border-border bg-card px-6">
      <div className="mx-auto flex w-full max-w-(--breakpoint-xl) items-center justify-between py-3">
        <div className="flex h-10 items-center gap-2">
          <Link
            to={DashboardRoute.fullPath}
            className="flex h-10 items-center gap-1"
          >
            <Logo />
          </Link>
          <Slash className="h-6 w-6 -rotate-12 stroke-[1.5px] text-primary/10" />
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger>
              <Button
                variant="ghost"
                className="gap-2 px-2 data-[state=open]:bg-primary/5"
              >
                <div className="flex items-center gap-2">
                  {user.avatarUrl ? (
                    <Image
                      className="h-8 w-8 rounded-full object-cover"
                      alt={user.username ?? user.email}
                      src={user.avatarUrl}
                      width={32}
                      height={32}
                    />
                  ) : (
                    <span className="h-8 w-8 rounded-full bg-linear-to-br from-lime-400 from-10% via-cyan-300 to-blue-500" />
                  )}

                  <p className="text-sm font-medium text-primary/80">
                    {user?.username || ''}
                  </p>
                  <span className="flex h-5 items-center rounded-full bg-primary/10 px-2 text-xs font-medium text-primary/80">
                    ${((user.wallet?.balanceCents ?? 0) / 100).toFixed(2)}
                  </span>
                </div>
                <span className="flex flex-col items-center justify-center">
                  <ChevronUp className="relative top-[3px] h-[14px] w-[14px] stroke-[1.5px] text-primary/60" />
                  <ChevronDown className="relative bottom-[3px] h-[14px] w-[14px] stroke-[1.5px] text-primary/60" />
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sideOffset={8}
              className="min-w-56 bg-card p-2"
            >
              <DropdownMenuLabel className="flex items-center text-xs font-normal text-primary/60">
                {t('nav.personalAccount')}
              </DropdownMenuLabel>
              <DropdownMenuItem className="h-10 w-full cursor-pointer justify-between rounded-md bg-secondary px-2">
                <div className="flex items-center gap-2">
                  {user.avatarUrl ? (
                    <Image
                      className="h-6 w-6 rounded-full object-cover"
                      alt={user.username ?? user.email}
                      src={user.avatarUrl}
                      width={24}
                      height={24}
                    />
                  ) : (
                    <span className="h-6 w-6 rounded-full bg-linear-to-br from-lime-400 from-10% via-cyan-300 to-blue-500" />
                  )}

                  <p className="text-sm font-medium text-primary/80">
                    {user.username || ''}
                  </p>
                </div>
                <Check className="h-[18px] w-[18px] stroke-[1.5px] text-primary/60" />
              </DropdownMenuItem>

              <DropdownMenuSeparator className="mx-0 my-2" />
              <DropdownMenuItem className="p-0 focus:bg-transparent">
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() =>
                    navigate({ to: BillingSettingsRoute.fullPath })
                  }
                >
                  {t('nav.topUpBalance')}
                </Button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex h-10 items-center gap-3">
          <Button
            variant={isChatOpen ? 'secondary' : 'ghost'}
            size="sm"
            className="hidden h-8 gap-1.5 lg:flex"
            onClick={onChatToggle}
          >
            <MessageSquare className="h-4 w-4" />
            <span className="text-sm">{t('interviews.orgChat.toggle')}</span>
          </Button>
          <NotificationBell />
          <a
            href="https://github.com/get-convex/convex-saas/tree/main/docs"
            className={cn(
              `${buttonVariants({ variant: 'outline', size: 'sm' })} group hidden h-8 gap-2 rounded-full bg-transparent px-2 pr-2.5 md:flex`,
            )}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-primary"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
            </svg>
            <span className="text-sm text-primary/60 transition group-hover:text-primary group-focus:text-primary">
              {t('nav.documentation')}
            </span>
          </a>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger>
              <Button variant="ghost" className="h-8 w-8 rounded-full">
                {user.avatarUrl ? (
                  <Image
                    className="min-h-8 min-w-8 rounded-full object-cover"
                    alt={user.username ?? user.email}
                    src={user.avatarUrl}
                    width={32}
                    height={32}
                  />
                ) : (
                  <span className="min-h-8 min-w-8 rounded-full bg-linear-to-br from-lime-400 from-10% via-cyan-300 to-blue-500" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              sideOffset={8}
              className="fixed -right-4 min-w-56 bg-card p-2"
            >
              <DropdownMenuItem className="group flex-col items-start focus:bg-transparent">
                <p className="text-sm font-medium text-primary/80 group-hover:text-primary group-focus:text-primary">
                  {user?.username || ''}
                </p>
                <p className="text-sm text-primary/60">{user?.email}</p>
              </DropdownMenuItem>

              <DropdownMenuItem
                className="group h-9 w-full cursor-pointer justify-between rounded-md px-2"
                onClick={() => navigate({ to: SettingsRoute.fullPath })}
              >
                <span className="text-sm text-primary/60 group-hover:text-primary group-focus:text-primary">
                  {t('nav.settings')}
                </span>
                <Settings className="h-[18px] w-[18px] stroke-[1.5px] text-primary/60 group-hover:text-primary group-focus:text-primary" />
              </DropdownMenuItem>

              <DropdownMenuItem
                className={cn(
                  'group flex h-9 justify-between rounded-md px-2 hover:bg-transparent',
                )}
              >
                <span className="w-full text-sm text-primary/60 group-hover:text-primary group-focus:text-primary">
                  {t('nav.theme')}
                </span>
                <ThemeSwitcher />
              </DropdownMenuItem>

              <DropdownMenuItem
                className={cn(
                  'group flex h-9 justify-between rounded-md px-2 hover:bg-transparent',
                )}
              >
                <span className="w-full text-sm text-primary/60 group-hover:text-primary group-focus:text-primary">
                  {t('nav.language')}
                </span>
                <LanguageSwitcher />
              </DropdownMenuItem>

              <DropdownMenuSeparator className="mx-0 my-2" />

              <DropdownMenuItem
                className="group h-9 w-full cursor-pointer justify-between rounded-md px-2"
                onClick={() => signOut()}
              >
                <span className="text-sm text-primary/60 group-hover:text-primary group-focus:text-primary">
                  {t('nav.logOut')}
                </span>
                <LogOut className="h-[18px] w-[18px] stroke-[1.5px] text-primary/60 group-hover:text-primary group-focus:text-primary" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-(--breakpoint-xl) items-center gap-3">
        <div
          className={cn(
            `flex h-12 items-center border-b-2`,
            isDashboardPath ? 'border-primary' : 'border-transparent',
          )}
        >
          <Link
            to={DashboardRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: 'ghost', size: 'sm' })} text-primary/80`,
            )}
          >
            {t('nav.jobs')}
          </Link>
        </div>
        <div
          className={cn(
            `flex h-12 items-center border-b-2`,
            isCandidatesPath ? 'border-primary' : 'border-transparent',
          )}
        >
          <Link
            to={CandidatesRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: 'ghost', size: 'sm' })} text-primary/80`,
            )}
          >
            {t('nav.candidates')}
          </Link>
        </div>
        <div
          className={cn(
            `flex h-12 items-center border-b-2`,
            isReferralsPath ? 'border-primary' : 'border-transparent',
          )}
        >
          <Link
            to={ReferralsRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: 'ghost', size: 'sm' })} text-primary/80`,
            )}
          >
            Referrals
          </Link>
        </div>
        <div
          className={cn(
            `flex h-12 items-center border-b-2`,
            isPipelineReportPath ? 'border-primary' : 'border-transparent',
          )}
        >
          <Link
            to={PipelineReportRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: 'ghost', size: 'sm' })} text-primary/80`,
            )}
          >
            Pipeline Report
          </Link>
        </div>
        <div
          className={cn(
            `flex h-12 items-center border-b-2`,
            isTimeToHirePath ? 'border-primary' : 'border-transparent',
          )}
        >
          <Link
            to={TimeToHireRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: 'ghost', size: 'sm' })} text-primary/80`,
            )}
          >
            Time to Hire
          </Link>
        </div>
        <div
          className={cn(
            `flex h-12 items-center border-b-2`,
            isQuestionBankPath ? 'border-primary' : 'border-transparent',
          )}
        >
          <Link
            to={QuestionBankRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: 'ghost', size: 'sm' })} text-primary/80`,
            )}
          >
            {t('nav.questionBank')}
          </Link>
        </div>
        <div
          className={cn(
            `flex h-12 items-center border-b-2`,
            isTemplatesPath ? 'border-primary' : 'border-transparent',
          )}
        >
          <Link
            to={TemplatesRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: 'ghost', size: 'sm' })} text-primary/80`,
            )}
          >
            {t('nav.templates')}
          </Link>
        </div>
        <div
          className={cn(
            `flex h-12 items-center border-b-2`,
            isSettingsPath ? 'border-primary' : 'border-transparent',
          )}
        >
          <Link
            to={SettingsRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: 'ghost', size: 'sm' })} text-primary/80`,
            )}
          >
            {t('nav.settings')}
          </Link>
        </div>
        <div
          className={cn(
            `flex h-12 items-center border-b-2`,
            isBillingPath ? 'border-primary' : 'border-transparent',
          )}
        >
          <Link
            to={BillingSettingsRoute.fullPath}
            className={cn(
              `${buttonVariants({ variant: 'ghost', size: 'sm' })} text-primary/80`,
            )}
          >
            {t('nav.billing')}
          </Link>
        </div>
      </div>
    </nav>
  )
}
